import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { IntentionDto, MAX_INTENTIONS } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

interface IntentionModalProps extends BaseModalProps {
  intention: IntentionDto | null
  activeCount: number
  onSaved: (intention: IntentionDto) => void
}

const IntentionModal = ({ isOpen, onClose, intention, activeCount, onSaved }: IntentionModalProps) => {
  const isEdit = intention !== null
  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState('')
  const [description, setDescription] = useState('')
  const [emoji, setEmoji] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(intention?.name ?? '')
    setPrompt(intention?.prompt ?? '')
    setDescription(intention?.description ?? '')
    setEmoji(intention?.emoji ?? '')
    setError(null)
    setIsSaving(false)
  }, [isOpen, intention])

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const canCreate = !isEdit && activeCount < MAX_INTENTIONS

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedPrompt = prompt.trim()
    const trimmedDescription = description.trim()
    const trimmedEmoji = emoji.trim()

    if (!trimmedName || !trimmedPrompt || !trimmedEmoji) {
      setError('Name, prompt, and emoji are required.')
      return
    }

    if (!isEdit && activeCount >= MAX_INTENTIONS) {
      setError(`You can have at most ${MAX_INTENTIONS} active intentions.`)
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const saved = isEdit
        ? await adminService.updateIntention(intention.publicId, {
            name: trimmedName,
            prompt: trimmedPrompt,
            description: trimmedDescription || null,
            emoji: trimmedEmoji,
          })
        : await adminService.createIntention({
            name: trimmedName,
            prompt: trimmedPrompt,
            description: trimmedDescription || null,
            emoji: trimmedEmoji,
          })
      onSaved(saved)
      onClose()
    } catch {
      setError(isEdit ? `Failed to save intention "${trimmedName}".` : 'Failed to create intention.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit intention' : 'Add intention'}</h2>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput required type="text" label="Name" name="intentionName" value={name} onChange={setName} />
          <TextInput
            required
            type="textArea"
            label="Prompt (action description)"
            name="intentionPrompt"
            value={prompt}
            onChange={setPrompt}
          />
          <TextInput
            type="textArea"
            label="Description"
            name="intentionDescription"
            value={description}
            onChange={setDescription}
          />
          <TextInput required type="text" label="Emoji" name="intentionEmoji" value={emoji} onChange={setEmoji} />

          {!canCreate && !isEdit && (
            <p className="roles-page-intro">Maximum of {MAX_INTENTIONS} active intentions reached.</p>
          )}
        </form>

        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}

        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSubmit}
            disabled={isSaving || (!isEdit && !canCreate)}
          >
            {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create intention'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default IntentionModal
